import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchCollections as fetchCollectionsService,
  fetchCollection as fetchCollectionService,
  fetchCollectionFormData as fetchCollectionFormDataService,
  createCollection as createCollectionService,
  updateCollection as updateCollectionService,
  deleteCollection as deleteCollectionService,
  fetchFollowedCollections as fetchFollowedCollectionsService,
  fetchCollectionsSummary as fetchCollectionsSummaryService,
  addItemToCollection as addItemToCollectionService,
  removeItemFromCollection as removeItemFromCollectionService,
  toggleFollowCollection as toggleFollowCollectionService,
} from "./collection.service";

import {
  GetCollectionsResponse,
  GetCollectionResponse,
  GetCollectionFormDataResponse,
  CreateCollectionResponse,
  UpdateCollectionResponse,
  DeleteCollectionResponse,
  GetFollowedCollectionsResponse,
  GetCollectionsSummaryResponse,
  AddItemResponse,
  RemoveItemResponse,
  ToggleFollowResponse,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  AddItemToCollectionRequest,
  RemoveItemFromCollectionRequest,
} from "./collection.types";

import { handleApiError } from "@/app/utils/handleApiError";
import { FilterRequest } from "../filters/filters.types";

/* ----------------------------------------
   GET COLLECTIONS
---------------------------------------- */
export const getCollections = createAsyncThunk<
  GetCollectionsResponse,
  { search?: string } | void,
  { rejectValue: string }
>("collection/getCollections", async (params, { rejectWithValue }) => {
  try {
    return await fetchCollectionsService(params || undefined);
  } catch (err) {
    return rejectWithValue(handleApiError(err));
  }
});

/* ----------------------------------------
   GET SINGLE COLLECTION
---------------------------------------- */
export const getCollection = createAsyncThunk<
  GetCollectionResponse,
  {
    idOrUuid: string | number;
    page?: number;
    filters?: Partial<FilterRequest>;
  },
  { rejectValue: string }
>(
  "collection/getCollection",
  async ({ idOrUuid, page, filters }, { rejectWithValue }) => {
    try {
      return await fetchCollectionService(idOrUuid, page, filters);
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  },
);

/* ----------------------------------------
   FORM DATA
---------------------------------------- */
export const getCollectionFormData = createAsyncThunk<
  GetCollectionFormDataResponse,
  number | undefined,
  { rejectValue: string }
>("collection/getCollectionFormData", async (id, { rejectWithValue }) => {
  try {
    return await fetchCollectionFormDataService(id);
  } catch (err) {
    return rejectWithValue(handleApiError(err));
  }
});

/* ----------------------------------------
   CREATE
---------------------------------------- */
export const createCollection = createAsyncThunk<
  CreateCollectionResponse,
  CreateCollectionRequest,
  { rejectValue: string }
>("collection/createCollection", async (data, { rejectWithValue }) => {
  try {
    return await createCollectionService(data);
  } catch (err) {
    return rejectWithValue(handleApiError(err));
  }
});

/* ----------------------------------------
   UPDATE
---------------------------------------- */
export const updateCollection = createAsyncThunk<
  UpdateCollectionResponse,
  {
    collectionId: number;
    data: UpdateCollectionRequest;
  },
  { rejectValue: string }
>(
  "collection/updateCollection",
  async ({ collectionId, data }, { rejectWithValue }) => {
    try {
      return await updateCollectionService(collectionId, data);
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  },
);

/* ----------------------------------------
   DELETE
---------------------------------------- */
export const deleteCollection = createAsyncThunk<
  DeleteCollectionResponse,
  number,
  { rejectValue: string }
>("collection/deleteCollection", async (collectionId, { rejectWithValue }) => {
  try {
    return await deleteCollectionService(collectionId);
  } catch (err) {
    return rejectWithValue(handleApiError(err));
  }
});

/* ----------------------------------------
   FOLLOWED COLLECTIONS
---------------------------------------- */
export const getFollowedCollections = createAsyncThunk<
  GetFollowedCollectionsResponse,
  { search?: string } | void,
  { rejectValue: string }
>("collection/getFollowedCollections", async (params, { rejectWithValue }) => {
  try {
    return await fetchFollowedCollectionsService(params || undefined);
  } catch (err) {
    return rejectWithValue(handleApiError(err));
  }
});

/* ----------------------------------------
   SUMMARY
---------------------------------------- */
export const getCollectionsSummary = createAsyncThunk<
  GetCollectionsSummaryResponse,
  { search?: string } | void,
  { rejectValue: string }
>("collection/getCollectionsSummary", async (params, { rejectWithValue }) => {
  try {
    return await fetchCollectionsSummaryService(params || undefined);
  } catch (err) {
    return rejectWithValue(handleApiError(err));
  }
});

/* ----------------------------------------
   ADD ITEM
---------------------------------------- */
export const addItemToCollection = createAsyncThunk<
  AddItemResponse,
  {
    collectionId: number;
    data: AddItemToCollectionRequest;
  },
  { rejectValue: string }
>(
  "collection/addItemToCollection",
  async ({ collectionId, data }, { rejectWithValue }) => {
    try {
      return await addItemToCollectionService(collectionId, data);
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  },
);

/* ----------------------------------------
   REMOVE ITEM
---------------------------------------- */
export const removeItemFromCollection = createAsyncThunk<
  RemoveItemResponse,
  {
    collectionId: number;
    data: RemoveItemFromCollectionRequest;
  },
  { rejectValue: string }
>(
  "collection/removeItemFromCollection",
  async ({ collectionId, data }, { rejectWithValue }) => {
    try {
      return await removeItemFromCollectionService(collectionId, data);
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  },
);

/* ----------------------------------------
   TOGGLE FOLLOW
---------------------------------------- */
export const toggleFollowCollection = createAsyncThunk<
  ToggleFollowResponse,
  number,
  { rejectValue: string }
>(
  "collection/toggleFollowCollection",
  async (collectionId, { rejectWithValue }) => {
    try {
      return await toggleFollowCollectionService(collectionId);
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  },
);
